<template>
    <tailwind-layout title="Self Reporting">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto mt-1">
                <div class="flex justify-between items-center gap-4">
                    <h1 class="text-lg text-left font-bold leading-7 text-gray-700 sm:text-3xl sm:truncate">
                        Self Reporting
                    </h1>
                    <inertia-link
                        :href="route('reports.self-credit-check')"
                        class="relative inline-flex shrink-0 items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                    >
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>

                <div v-if="showHeader" class="flex gap-4 mt-4">
                    <div class="flex flex-col bg-white p-4 rounded-lg shadow-md flex-1">
                        <div class="flex">
                            <h2 class="font-bold leading-tight">Your Progress</h2>
                            <span class="text-sm ml-3 leading-tight">{{ completedCount }} of {{ totalCount }} steps completed</span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div class="bg-blue-600 h-2 rounded-full transition-all" :style="`width: ${completionPercentage}%`"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="relative">
            <!-- Experian attempts warning, shown on the steps that talk to Experian. -->
            <div v-if="showExperianAttempts && experian_attempt && experian_attempt.attempts < 3" class="mb-6">
                <alert-card :type="'warning'">
                    <div>
                        <h3 class="text-sm font-medium text-yellow-800">{{ experian_attempt.attempts }} Attempts Remaining</h3>
                        <div v-if="experian_attempt.attempts > 0" class="mt-2 text-sm text-yellow-700">
                            You have {{ experian_attempt.attempts }} remaining attempts to generate a credit report within the next 24 hours.
                            <button type="button" class="text-blue-600 underline" @click="showExperianHelp = true">Having Trouble? Get Tips</button>
                        </div>
                        <div v-else class="mt-2 text-sm text-yellow-700">
                            You are currently locked out due to multiple unsuccessful credit report attempts. You will be able to try again after {{ experian_attempt.remaining_lockout_time }}.
                            <button type="button" class="text-blue-600 underline" @click="showExperianHelp = true">Having Trouble? Get Tips</button>
                        </div>
                    </div>
                </alert-card>
            </div>

            <div class="flex gap-4 mt-6 min-w-0">
                <!-- Progress rail -->
                <div v-if="showNavigationMenu" class="flex-shrink-0 sticky top-20 self-start hidden lg:flex">
                    <div class="flex-none p-4 bg-white rounded-lg shadow-md">
                        <nav aria-label="Progress" class="sticky top-20 w-full max-w-64">
                            <div class="flex flex-col">
                                <h2 class="text-xl font-bold leading-tight">Your Progress</h2>
                                <h4 class="text-sm font-semibold">{{ completedCount }} of {{ totalCount }} steps completed</h4>
                            </div>

                            <ol role="list" class="overflow-hidden mt-6">
                                <li
                                    v-for="(step, index) in steps"
                                    :key="step.key"
                                    :class="[index !== steps.length - 1 ? 'pb-10' : '', 'relative']"
                                >
                                    <div
                                        v-if="index !== steps.length - 1"
                                        class="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5"
                                        :class="step.complete ? 'bg-green-600' : 'bg-gray-300'"
                                        aria-hidden="true"
                                    />

                                    <!-- Completed, or reachable: a real link -->
                                    <component
                                        :is="step.available ? 'Link' : 'div'"
                                        v-bind="step.available ? { href: step.href } : {}"
                                        class="group relative flex items-start w-full text-left p-2 rounded -m-2"
                                        :class="step.available ? 'hover:bg-gray-50' : 'cursor-not-allowed'"
                                        :aria-current="step.key === currentStep ? 'step' : null"
                                    >
                                        <span class="flex h-9 items-center" aria-hidden="true">
                                            <span
                                                v-if="step.complete"
                                                class="relative z-10 flex size-8 items-center justify-center rounded-full bg-green-600"
                                            >
                                                <font-awesome-icon :icon="faCheck" class="text-white" aria-hidden="true" />
                                            </span>
                                            <span
                                                v-else
                                                class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 bg-white"
                                                :class="step.available ? 'border-blue-600' : 'border-gray-300'"
                                            >
                                                <span class="size-2.5 rounded-full" :class="step.available ? 'bg-blue-600' : 'bg-transparent'" />
                                            </span>
                                        </span>
                                        <span class="ml-4 flex min-w-0 flex-col">
                                            <span
                                                class="text-sm font-bold"
                                                :class="[
                                                    step.key === currentStep ? 'text-indigo-600' : (step.available || step.complete) ? 'text-gray-600' : 'text-gray-400'
                                                ]"
                                            >
                                                {{ step.number }}. {{ step.name }}
                                                <span
                                                    v-if="step.optional"
                                                    class="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700"
                                                    data-test="optional"
                                                >Optional</span>
                                                <span
                                                    v-if="step.free"
                                                    class="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-700"
                                                    data-test="free"
                                                >Free</span>
                                            </span>
                                            <span
                                                class="text-sm"
                                                :class="(step.available || step.complete) ? 'text-gray-500' : 'text-gray-400'"
                                            >
                                                {{ step.description }}
                                            </span>
                                        </span>
                                    </component>
                                </li>
                            </ol>

                            <div class="mt-6 pt-4 border-t border-gray-200">
                                <h3 class="text-xs font-semibold text-gray-600 mb-2">Status:</h3>
                                <div class="space-y-1.5 text-xs">
                                    <div class="flex items-center text-gray-600">
                                        <span class="flex h-3 w-3 items-center justify-center rounded-full bg-green-600 mr-2">
                                            <font-awesome-icon :icon="faCheck" class="text-white text-[8px]" />
                                        </span>
                                        <span>Completed</span>
                                    </div>
                                    <div class="flex items-center text-gray-600">
                                        <span class="flex h-3 w-3 items-center justify-center rounded-full border-2 border-blue-600 bg-white mr-2">
                                            <span class="h-1 w-1 rounded-full bg-blue-600"></span>
                                        </span>
                                        <span>Available / Next</span>
                                    </div>
                                    <div class="flex items-center text-gray-600">
                                        <span class="flex h-3 w-3 items-center justify-center rounded-full border-2 border-gray-300 bg-white mr-2">
                                            <span class="h-1 w-1 rounded-full bg-transparent"></span>
                                        </span>
                                        <span>Not yet available</span>
                                    </div>
                                </div>
                            </div>
                        </nav>
                    </div>
                </div>

                <div class="flex-1 min-w-0">
                    <slot />
                </div>
            </div>
        </div>

        <experian-info-popup v-model:open="showExperianHelp" />
    </tailwind-layout>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons'

import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import AlertCard from '@/Shared/AlertCard.vue'
import ExperianInfoPopup from '@/Shared/ExperianInfoPopup.vue'
import { buildReportSteps, reportProgress } from './reportSteps'

/**
 * 86bc1ymu6: the self credit check's own progress chrome.
 *
 * Deliberately standalone rather than reusing Applications/ApplicationLayout
 * and NavigationNew: those are driven by an Application's `required_steps`
 * JSON and its property, neither of which a self credit check has. This owns
 * its four fixed steps and derives their state from the report and whether
 * the user's Experian verification stands.
 *
 * Step availability mirrors ReportController's and StripeController's own
 * guards — terms, payment, verification, then the report — so the rail never offers a
 * link that would just bounce the user somewhere else.
 */
export default {
    components: {
        TailwindLayout,
        AlertCard,
        ExperianInfoPopup,
        FontAwesomeIcon,
        Link,
    },
    props: {
        report: {
            type: Object,
            required: true,
        },
        currentStep: {
            type: String,
            default: 'dashboard',
        },
        experian_attempt: {
            type: Object,
            default: () => ({}),
        },
        showNavigationMenu: {
            type: Boolean,
            default: true,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        showExperianAttempts: {
            type: Boolean,
            default: false,
        },
        identityVerified: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Options API: the template resolves names off the instance, so
            // module-scope imports have to be exposed here or every icon
            // silently renders as nothing.
            faCheck,
            faChevronLeft,
            showExperianHelp: false,
        }
    },
    computed: {
        steps() {
            return buildReportSteps(this.report, { identityVerified: this.identityVerified })
        },
        completedCount() {
            return reportProgress(this.steps).completed
        },
        // 86bc8uyd2: optional Applicant Boost steps are listed but not counted.
        totalCount() {
            return reportProgress(this.steps).total
        },
        completionPercentage() {
            return reportProgress(this.steps).percentage
        },
    },
}
</script>
